<template>
  <transition name="fade2" mode="out-in" appear>
    <header v-if="picture" :key="picture" class="container-fixed">
      <div class="row banner" :style="`background-image: url('${picture}');`">
        <div class="col-xl-6 col-l-6 col-m-12 col-s-12 col-xs-12 inner">
          <transition name="fade" mode="out-in" appear>
            <h1 :key="headline" class="brand" v-html="headline"></h1>
          </transition>
          <transition name="cross" mode="out-in" appear>
            <h2 v-if="subheadline" :key="subheadline" v-html="subheadline"></h2>
          </transition>
        </div>
      </div>
    </header>
  </transition>
</template>

<script>
import {
  PRODUCT_DETAILS,
  PRODUCT_ORDER_SELECTION,
  PRODUCT_ORDER_SUMMARY,
  PRODUCT_ORDER_CONFIRMATION,
  PRODUCT_ORDER_SUMMARY_OPTIN,
  PRODUCT_ORDER_CONFIRMATION_OPTIN,
  STOCK_PRODUCT_CHANGE_SUMMARY,
  STOCK_PRODUCT_CHANGE_CONFIRMATION,
} from '@route-types';
import { metas } from '@/utils/navigation';
import emitter from '@/plugins/emitter';
import { mapGetters } from 'vuex';

const bannerContext = require.context('@/assets/images/banners', false, /\.jpg$/);
const wholesaleBannerContext = require.context('@/assets/images/banners/wholesale', false, /\.jpg$/);

export default {
  name: 'UiBanner',

  data() {
    return {
      overridePicture: null,
      headlineOverride: null,
      subheadlineOverride: null,
      groups: [
        [
          PRODUCT_DETAILS,
          PRODUCT_ORDER_SELECTION,
          PRODUCT_ORDER_SUMMARY,
          PRODUCT_ORDER_CONFIRMATION,
          PRODUCT_ORDER_SUMMARY_OPTIN,
          PRODUCT_ORDER_CONFIRMATION_OPTIN,
        ],
        [STOCK_PRODUCT_CHANGE_SUMMARY, STOCK_PRODUCT_CHANGE_CONFIRMATION],
      ],
    };
  },

  computed: {
    ...mapGetters('system', ['isTelekom']),

    headline() {
      if (this.headlineOverride) return this.headlineOverride;
      // first, we check if there is an explicit override for the current page
      if (this.$te(`common.banner_headline_${this.$route.name}`)) {
        return this.$t(`common.banner_headline_${this.$route.name}`);
      }
      // if not, we take the first parent and look up the i18n text:
      return this.translate(metas[this.$route.name].path[0]);
    },

    subheadline() {
      if (this.subheadlineOverride) return this.subheadlineOverride;

      // first, we check if there is an explicit override for the current page
      if (this.$te(`common.banner_subheadline_${this.$route.name}`)) {
        return this.$t(`common.banner_subheadline_${this.$route.name}`);
      }

      // otherwise check if the current name is equal to the headline
      if (this.$route.name === metas[this.$route.name].path[0]) return null;

      // if not, we take the look up the i18n text: (can be null)
      return this.translate(this.$route.name);
    },

    picture() {
      if (this.overridePicture) {
        return `delegates/product/content/${this.overridePicture}`;
      }
      return this.metaPicture;
    },

    metaPicture() {
      const meta = metas[this.$route.name];
      if (this.$te(meta.bannerLocaleOverride)) return `delegates/background/images/${this.$t(meta.bannerLocaleOverride)}`;
      if (!meta.banner) return null;
      if (!this.isTelekom) return wholesaleBannerContext(`./${meta.banner}`);
      return bannerContext(`./${meta.banner}`);
    },
  },

  watch: {
    $route: {
      handler: 'onRouteChanged',
      immediate: true,
    },
  },

  created() {
    // enable external update of the subheadline and picture (e.g. to add the selected product)
    // just call emitter.emit('setBannerSubheadline', 'payload'); in another component
    emitter.on('setBannerHeadline', this.onSetBannerHeadline);
    emitter.on('setBannerSubheadline', this.onSetBannerSubheadline);
    emitter.on('setBannerPicture', this.onSetBannerPicture);
  },

  beforeDestroy() {
    emitter.off('setBannerHeadline', this.onSetBannerHeadline);
    emitter.off('setBannerSubheadline', this.onSetBannerSubheadline);
    emitter.off('setBannerPicture', this.onSetBannerPicture);
  },

  methods: {
    onRouteChanged(to, from) {
      if (!from) return;
      const group = this.groups.find(items => items.includes(from.name));
      if (group && group.includes(to.name)) return; // same group: do not reset directly!
      this.headlineOverride = null;
      this.subheadlineOverride = null;
      this.overridePicture = null;
    },

    onSetBannerHeadline(payload) {
      this.headlineOverride = payload;
    },

    onSetBannerSubheadline(payload) {
      this.subheadlineOverride = payload;
    },

    onSetBannerPicture(payload) {
      this.overridePicture = payload;
    },

    translate(key) {
      if (this.$te(`common.breadcrumb_${key}`)) {
        return this.$t(`common.breadcrumb_${key}`);
      }
      if (this.$te(`common.navigation_${key}`)) {
        return this.$t(`common.navigation_${key}`);
      }
      return null;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.banner {
  height: 194px;
  background-repeat: no-repeat;
  background-position: right center;
  background-size: cover;

  > div {
    height: 100%;
    overflow: hidden;
    background-color: rgba(226, 0, 116, 0.8);
  }

  h1 {
    margin-top: 6px;
    color: #fff;
  }
  h2 {
    color: #fff;
  }
}

@media screen and (min-width: #{$grid-extra-large-min}px) {
  .banner {
    height: 250px;
    background-color: $color-brand;
  }
}

@media screen and (max-width: #{$grid-large-max}px) {
  .banner {
    h1.brand {
      font-size: 40px;
      font-weight: 700;
    }
    h2 {
      font-size: 28px;
    }
  }
}

@media screen and (max-width: #{$grid-small-max}px) {
  .banner {
    height: auto;
    background-image: url('~@/assets/images/banners/bg.jpg') !important;

    > div {
      background-color: transparent;
    }
    h1.brand {
      font-size: 30px;
    }
    h2 {
      font-size: 25px;
      white-space: normal;
    }
  }
}
</style>
